import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  AcademicCapIcon,
  CalendarIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getCourse, createCourse, updateCourse } from "../../../../api/hse/competency";
import { getCompetencyDefinitions } from "../../../../api/hse/competency";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Course Edit - Create or edit course
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyCourseEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [competencyDefinitions, setCompetencyDefinitions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    competency_definition_id: null,
    start_date: "",
    end_date: "",
    max_participants: null,
    location: "",
    instructor: "",
    status: "upcoming",
  });

  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: isNew ? "create" : "edit" });

  useEffect(() => {
    if (!isNew) {
      fetchCourse();
    }
    fetchCompetencyDefinitions();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getCourse(id);
      const courseData = response.course || response;
      setFormData({
        name: courseData.name || "",
        description: courseData.description || "",
        competency_definition_id: courseData.competency_definition_id || null,
        start_date: courseData.start_date ? courseData.start_date.split("T")[0] : "",
        end_date: courseData.end_date ? courseData.end_date.split("T")[0] : "",
        max_participants: courseData.max_participants || null,
        location: courseData.location || "",
        instructor: courseData.instructor || "",
        status: courseData.status || "upcoming",
      });
    } catch (err) {
      console.error("Error fetching course:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kurs"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetencyDefinitions = async () => {
    try {
      const response = await getCompetencyDefinitions({ is_active: true });
      setCompetencyDefinitions(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching competency definitions:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value ? parseInt(value) : null) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t("Navn er påkrevd"));
      return;
    }

    try {
      setSaving(true);
      
      let response;
      if (isNew) {
        response = await createCourse(formData);
        toast.success(t("Kurs opprettet"));
        navigate(`/dashboard/hse-ks/competency/courses/${response.course?.id || response.id}`);
      } else {
        response = await updateCourse(id, formData);
        toast.success(t("Kurs oppdatert"));
        navigate(`/dashboard/hse-ks/competency/courses/${id}`);
      }
    } catch (err) {
      console.error("Error saving course:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Kunne ikke lagre kurs");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency/courses");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kurs")]} />;
  }

  return (
    <HsePageChrome
      title={isNew ? t("Nytt kurs") : t("Rediger kurs")}
      subtitle={t("Kursskjema")}
      backTo={isNew ? "/dashboard/hse-ks/competency/courses" : `/dashboard/hse-ks/competency/courses/${id}`}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <CompetencySubnav />
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Grunnleggende informasjon")}</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t("Navn")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("F.eks. Førstehjelp kurs, Sikkerhetskurs, etc.")}
                  />
                </div>

                {/* Competency Definition */}
                <div className="sm:col-span-2">
                  <label htmlFor="competency_definition_id" className="block text-sm font-medium text-gray-700">
                    {t("Relatert kompetanse")}
                  </label>
                  <select
                    name="competency_definition_id"
                    id="competency_definition_id"
                    value={formData.competency_definition_id || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">{t("Ingen")}</option>
                    {competencyDefinitions.map((def) => (
                      <option key={def.id} value={def.id}>
                        {def.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                    {t("Startdato")}
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    id="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                    {t("Sluttdato")}
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    id="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {/* Max Participants */}
                <div>
                  <label htmlFor="max_participants" className="block text-sm font-medium text-gray-700">
                    {t("Maks deltakere")}
                  </label>
                  <input
                    type="number"
                    name="max_participants"
                    id="max_participants"
                    min="1"
                    value={formData.max_participants || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    {t("Status")}
                  </label>
                  <select
                    name="status"
                    id="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="upcoming">{t("Kommende")}</option>
                    <option value="ongoing">{t("Pågående")}</option>
                    <option value="completed">{t("Fullført")}</option>
                    <option value="cancelled">{t("Avlyst")}</option>
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    {t("Sted")}
                  </label>
                  <input
                    type="text"
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("F.eks. Kurslokale 1, Online, etc.")}
                  />
                </div>

                {/* Instructor */}
                <div>
                  <label htmlFor="instructor" className="block text-sm font-medium text-gray-700">
                    {t("Instruktør")}
                  </label>
                  <input
                    type="text"
                    name="instructor"
                    id="instructor"
                    value={formData.instructor}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Navn på instruktør")}
                  />
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t("Beskrivelse")}
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Beskriv kurset...")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/hse-ks/competency/courses")}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? t("Lagrer...") : isNew ? t("Opprett") : t("Lagre endringer")}
            </button>
          </div>
        </form>
      </div>
    </HsePageChrome>
  );
};

